import { Box, Typography } from '@mui/material';
import type { ParsedAchievement } from '../achievementHelpers';

interface AchievementBadgeProps {
  parsed: ParsedAchievement;
  count: number;
  isSelected: boolean;
  onClick: () => void;
}

// Returns border, background, glow, and text color for each tier.
// All-time: animated shimmer gradient border + gold glow — the most coveted
// Monthly:  solid rank-tinted border, slightly elevated background
// Weekly:   original subtle purple style, unchanged
function getBadgeStyle(parsed: ParsedAchievement): {
  background: string;
  border: string;
  boxShadow: string;
  selectedBorder: string;
  selectedBackground: string;
  countColor: string;
  isAlltime: boolean;
} {
  if (parsed.periodType === 'alltime') {
    const RANK_GLOW: Record<number, string> = {
      1: '0 0 8px 2px rgba(255,215,0,0.45)',
      2: '0 0 8px 2px rgba(192,192,192,0.45)',
      3: '0 0 8px 2px rgba(205,127,50,0.45)',
    };
    const RANK_BORDER_COLOR: Record<number, string> = {
      1: 'rgba(255,215,0,0.8)',
      2: 'rgba(210,210,210,0.8)',
      3: 'rgba(205,127,50,0.8)',
    };
    const RANK_SELECTED_BORDER: Record<number, string> = {
      1: 'rgba(255,215,0,1)',
      2: 'rgba(220,220,220,1)',
      3: 'rgba(205,127,50,1)',
    };
    return {
      background: 'rgba(255,255,255,0.09)',
      border: `1px solid ${RANK_BORDER_COLOR[parsed.rank] ?? 'rgba(255,215,0,0.6)'}`,
      boxShadow: RANK_GLOW[parsed.rank] ?? RANK_GLOW[1],
      selectedBorder: `1.5px solid ${RANK_SELECTED_BORDER[parsed.rank] ?? 'rgba(255,215,0,1)'}`,
      selectedBackground: 'rgba(255,255,255,0.14)',
      countColor: '#ffd700',
      isAlltime: true,
    };
  }

  if (parsed.periodType === 'month') {
    const RANK_BORDER_COLOR: Record<number, string> = {
      1: 'rgba(255,200,50,0.55)',
      2: 'rgba(180,180,195,0.55)',
      3: 'rgba(185,110,60,0.55)',
    };
    const RANK_SELECTED_BORDER: Record<number, string> = {
      1: 'rgba(255,200,50,0.9)',
      2: 'rgba(180,180,195,0.9)',
      3: 'rgba(185,110,60,0.9)',
    };
    return {
      background: 'rgba(255,255,255,0.07)',
      border: `1px solid ${RANK_BORDER_COLOR[parsed.rank] ?? 'rgba(255,200,50,0.4)'}`,
      boxShadow: 'none',
      selectedBorder: `1.5px solid ${RANK_SELECTED_BORDER[parsed.rank] ?? 'rgba(255,200,50,0.9)'}`,
      selectedBackground: 'rgba(255,255,255,0.11)',
      countColor: '#dda0f0',
      isAlltime: false,
    };
  }

  // Weekly — original style
  return {
    background: 'rgba(255,255,255,0.06)',
    border: '0.5px solid rgba(206,147,216,0.2)',
    boxShadow: 'none',
    selectedBorder: '1.5px solid rgba(206,147,216,0.6)',
    selectedBackground: 'rgba(255,255,255,0.1)',
    countColor: '#dda0f0',
    isAlltime: false,
  };
}

export default function AchievementBadge({ parsed, count, isSelected, onClick }: AchievementBadgeProps) {
  const style = getBadgeStyle(parsed);

  return (
    <Box
      onClick={onClick}
      role="button"
      aria-pressed={isSelected}
      aria-label={parsed.label}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        background: isSelected ? style.selectedBackground : style.background,
        border: isSelected ? style.selectedBorder : style.border,
        boxShadow: style.boxShadow,
        borderRadius: '20px',
        px: '10px',
        py: '5px',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background 0.15s ease, border 0.15s ease',
        // Subtle pulse on all-time badges to signal their prestige
        ...(style.isAlltime && !isSelected && {
          animation: 'alltimePulse 3s ease-in-out infinite',
          '@keyframes alltimePulse': {
            '0%, 100%': { boxShadow: style.boxShadow },
            '50%': {
              boxShadow: style.boxShadow.replace(/0\.45\)/, '0.7)').replace(/0\.4\)/, '0.65)'),
            },
          },
        }),
      }}
    >
      <Typography sx={{ fontSize: 16, lineHeight: 1 }}>
        {parsed.icon}
      </Typography>
      {count > 1 && (
        <Typography sx={{ fontSize: 13, color: style.countColor, fontWeight: 500 }}>
          ×{count}
        </Typography>
      )}
    </Box>
  );
}